import { useRef, useState, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { Form, useFormikContext } from 'formik'
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Tooltip,
  useTheme,
  Link,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'

import { LinkIcon } from 'src/assets/icons'
import { MediaInputComponent } from './media-input.component'
import { MediaImage } from './media-image.component'
import { MainImageSwitch } from './main-image-switch.component'
import { StubImageButton } from './stub-image-button.component'
import {
  DEFAULT_STOCK_IMAGE_TYPE,
  DEFAULT_STOCK_VIDEO_TYPE,
  StockImagesModal,
} from '../stock-images'
import {
  EAccountSubscriptionPlans,
  ETranslationNs,
  ICustomIconFile,
  IMediaFileWithId,
  ISetStockImage,
  ISetStockVideo,
} from 'src/types'

import { useAccount, useItemsRemoveConfirm } from 'src/hooks'
import { AppEvents } from 'src/constants'
import useStyles from './styles'
import { HelperWrapper } from '../helper-wrapper'
import { ShowItemSwitch } from './show-item-switch.component'
import { MediaInputContext } from './context/media.context'
import { joinAbsRoute } from 'src/common/utils'
import { ROUTES } from 'src/constants'
import { ImageCropperModal } from '../image-cropper'

interface IProps {
  mainImageSwitch?: boolean
  noJourneyOptions?: boolean
  showSwitch?: boolean
  onClose: () => void
  scanToEarnMode?: boolean
}

const MediaInputForm = ({
  mainImageSwitch,
  noJourneyOptions,
  showSwitch,
  onClose,
  scanToEarnMode,
}: IProps) => {
  const [t] = useTranslation([ETranslationNs.Common, ETranslationNs.Form])
  const inputRef = useRef<HTMLInputElement | null>(null)
  const { values, errors, handleChange, setFieldValue } = useFormikContext<IMediaFileWithId>()
  const { media, handleDelete } = useContext(MediaInputContext)
  const classes = useStyles({})

  const [imageToCrop, setImageToCrop] = useState<ICustomIconFile | null>(null)
  const [open, setOpen] = useState(false)
  const [stockOpen, setStockOpen] = useState(false)

  const { setRemoveConfirm } = useItemsRemoveConfirm()
  const { accountId } = useParams()

  const { data: account, isLoading } = useAccount()

  const freeAccount =
    account?.subscription_plan === EAccountSubscriptionPlans.free || isLoading || !account

  const handleClose = () => {
    setOpen(false)
    setImageToCrop(null)
  }

  const handleSetImage = (image: ICustomIconFile) => {
    setFieldValue('file', image.file)
    setFieldValue('link', image.icon)
    setFieldValue('link_small', null)
    setFieldValue('link_medium', null)
    setFieldValue('type', DEFAULT_STOCK_IMAGE_TYPE)
    setOpen(false)
    setImageToCrop(null)
  }

  const handleDeleteItem = () => async () => {
    if (!values.link || !values.editId || !media) return

    setRemoveConfirm({
      handleConfirm: async () => {
        handleDelete(
          'media',
          media.filter(({ link: selected }) => values.link !== selected),
        )
      },
      itemsTitles: [],
      itemsRelations: [],
      itemsType: t('confirm-modal-media-item.v'),
    })
    onClose()
  }

  const handleStockOpen = () => {
    setStockOpen(true)
  }
  const handleStockClose = () => {
    setStockOpen(false)
  }
  const handleStockChoose: ISetStockImage = (img) => {
    setOpen(true)
    setImageToCrop({ icon: img, file: null })
    setStockOpen(false)
  }

  const handleStockVideChoose: ISetStockVideo = (video) => {
    setFieldValue('link', video.link)
    setFieldValue('link_medium', video.link_medium)
    setFieldValue('link_small', video.link_small)
    setFieldValue('type', DEFAULT_STOCK_VIDEO_TYPE)
    setStockOpen(false)
  }

  const handleInputClick = () => {
    inputRef.current?.click()
  }

  const handleImageDeleteClick = () => {
    setFieldValue('link', '')
    setFieldValue('file', null)
    setFieldValue('type', '')
  }
  const theme = useTheme()

  const handleCopyClick = () => {
    const detail = { message: t('clipboard-copied-notification.v') }
    const event = new CustomEvent(AppEvents.HTTP_SUCCESS, { detail })
    document.dispatchEvent(event)

    navigator.clipboard.writeText(values.link)
  }

  return (
    <>
      <Form className={classes.mediaForm}>
        <div className={classes.modalHeader}>
          <Typography component="h3" className={classes.modalTitle}>
            {values.editId ? (
              <HelperWrapper t="media-input-title-edit" ns={ETranslationNs.Form} isColorInherited />
            ) : (
              <HelperWrapper t="media-input-title-add" ns={ETranslationNs.Form} isColorInherited />
            )}
          </Typography>
        </div>
        <div className={classes.modalContentWrapper}>
          <div className={classes.modalImageWrapper}>
            <MediaInputComponent
              inputRef={inputRef}
              setCropModalOpen={setOpen}
              setImageToCrop={setImageToCrop}
            />
            {values?.link ? (
              <MediaImage
                type={values.type}
                link={values.link}
                link_medium={values.link_medium}
                alt={values.title ?? 'media file'}
                handleRemove={handleImageDeleteClick}
                showPreview
              />
            ) : (
              <Tooltip
                title={t('media-input-stub-label.v', { ns: ETranslationNs.Form })}
                placement="bottom"
                disableInteractive
              >
                <div>
                  <StubImageButton isModal handleClick={handleInputClick} />
                </div>
              </Tooltip>
            )}
            <Typography className={classes.warningText} color="warning">
              {errors.link && <HelperWrapper t={errors.link} />}&nbsp;
            </Typography>
            <Button size="small" onClick={handleStockOpen} variant="contained">
              <HelperWrapper t="suggest-button-text" ns={ETranslationNs.Form} isColorInherited />
            </Button>
          </div>
          <TextField
            autoComplete="off"
            fullWidth
            variant="standard"
            InputLabelProps={{ shrink: true }}
            label={
              <HelperWrapper
                t="title-input-label"
                ns={ETranslationNs.Form}
                isColorInherited
                usePortal
                isMandatory
              />
            }
            value={values.title}
            onChange={handleChange}
            helperText={errors.title && <HelperWrapper t={errors.title} />}
            FormHelperTextProps={{ error: true }}
            name="title"
          />
          <TextField
            autoComplete="off"
            fullWidth
            disabled={!!values.file}
            variant="standard"
            name="link"
            InputLabelProps={{ shrink: true }}
            label={
              <HelperWrapper
                t="modal-url-input-label"
                ns={ETranslationNs.Form}
                isColorInherited
                usePortal
              />
            }
            value={values.file ? '' : values.link}
            onChange={handleChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    className={classes.linkInputButton}
                    disabled={!!values.file || !values.link}
                    onClick={handleCopyClick}
                  >
                    <ContentCopyIcon className={classes.linkIcon} />
                  </IconButton>
                  <IconButton
                    className={classes.linkInputButton}
                    disabled={!!values.file || !values.link}
                    component="a"
                    href={values.link?.includes('http') ? values.link : `//${values.link}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <LinkIcon className={classes.linkIcon} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {!noJourneyOptions && (
            <div className={classes.additionalSection}>
              <Typography className={classes.cardTitle}>
                <HelperWrapper
                  t="journey-page-title-label"
                  isColorInherited
                  size={12}
                  verticalPosition={15}
                />
              </Typography>
              {showSwitch && <ShowItemSwitch />}
              {mainImageSwitch && <MainImageSwitch showSwitch={showSwitch} />}
              {freeAccount && (
                <div style={{ color: theme.palette.custom.darkBlue2, fontSize: 12, marginTop: 12 }}>
                  <HelperWrapper t="main-image-label-upgrade" ns={ETranslationNs.Form} />
                  &nbsp;
                  <Link
                    component={RouterLink}
                    to={joinAbsRoute(ROUTES.ACCOUNTS, accountId, `${ROUTES.ACCOUNT}?tab=billing`)}
                  >
                    <HelperWrapper
                      t="upgrade-button-text"
                      ns={ETranslationNs.Form}
                      isColorInherited
                    />
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
        <Box
          sx={{
            display: 'flex',
            justifyContent: values.editId ? 'space-evenly' : 'center',
            width: values.editId ? '100%' : 'auto',
          }}
        >
          {values.editId ? (
            <>
              <Button variant="outlined" type="button" onClick={handleDeleteItem()}>
                <HelperWrapper t="delete-button-text" ns={ETranslationNs.Common} isColorInherited />
              </Button>
              <Button variant="contained" type="submit">
                <HelperWrapper
                  t="media-input-title-edit"
                  ns={ETranslationNs.Form}
                  isColorInherited
                />
              </Button>
            </>
          ) : (
            <Button variant="contained" fullWidth type="submit">
              <HelperWrapper t="media-input-title-add" ns={ETranslationNs.Form} isColorInherited />
            </Button>
          )}
        </Box>
        {/* <AccountFreeTooltip
                  message={}
          /> */}
      </Form>
      <StockImagesModal
        open={stockOpen}
        handleClose={handleStockClose}
        setImage={handleStockChoose}
        setVideo={handleStockVideChoose}
        withVideo
      />
      <ImageCropperModal
        handleClose={handleClose}
        open={open}
        setImage={handleSetImage}
        imageToCrop={imageToCrop}
      />
    </>
  )
}

export { MediaInputForm }
